import { pricesContract } from "@openrift/shared/contracts/prices";
import { priceLookupFromMap } from "@openrift/shared/price-lookup";
import type { PriceLookup, PricesResponse } from "@openrift/shared/types/api/pricing";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { pricesKeys } from "@/features/cards/lib/cards-query-keys";
import { serverCacheKeys } from "@/lib/query-keys";
import { serverCache } from "@/lib/server-cache";
import { apiOrpcClient, browserApiOrpcClient } from "@/lib/server-fns/orpc-client";

export function readPricesFromServerCache(): Promise<PricesResponse> {
  return serverCache.query({
    queryKey: serverCacheKeys.prices,
    queryFn: () => apiOrpcClient(pricesContract).prices(),
  });
}

const fetchPrices = createServerFn({ method: "GET" }).handler(() => readPricesFromServerCache());

// Goes directly to /api/v1/prices so Cloudflare can serve it from the edge cache.
function fetchPricesFromEdge(): Promise<PricesResponse> {
  return browserApiOrpcClient(pricesContract).prices();
}

// Do not use `pricesQueryOptions` on the server here: it would dehydrate the whole
// catalog price map into SSR HTML for a few JSON-LD offers.
export function fetchPricesForSeo(): Promise<PricesResponse> {
  return fetchPrices();
}

export const pricesQueryOptions = queryOptions({
  queryKey: pricesKeys.all,
  queryFn: () => (globalThis.window === undefined ? fetchPrices() : fetchPricesFromEdge()),
  staleTime: 30 * 60 * 1000, // 30 minutes
  refetchOnWindowFocus: false,
  select: (response: PricesResponse): PriceLookup => priceLookupFromMap(response.prices),
});
